import { useMemo, useState } from 'react';
import { apiFetch } from '../../../../utils/api';
import { sortFeatureValues } from '../../../../utils/sortingUtils';
import { ManageFeatureModal } from '../Modals';
import ActionsMenu from '../../../../components/ActionsMenu';
import useUsageDeleteConfirm from '../../../../hooks/useUsageDeleteConfirm';

/**
 * FeatureNode - Represents a single feature with its values in the tree
 */
export default function FeatureNode({ feature, onRefresh, onRuleCellsChanged, onSearchFeature, onSearchValue, familyId }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [featureMenuOpen, setFeatureMenuOpen] = useState(false);
    
    const [hoveredValId, setHoveredValId] = useState(null);
    const [openValMenuId, setOpenValMenuId] = useState(null);
    const [editingValId, setEditingValId] = useState(null);
    const [editingValText, setEditingValText] = useState('');
    const [newValText, setNewValText] = useState('');
    const [isAddingValue, setIsAddingValue] = useState(false);
    const [pendingValues, setPendingValues] = useState([]);

    const displayValues = useMemo(() => {
        const server = Array.isArray(feature.rawValues) ? feature.rawValues : [];
        const pendingById = new Map(pendingValues.map(v => [v.id, v]));
        const serverIds = new Set(server.map(v => v.id));
        const merged = server.map(v => {
            const pending = pendingById.get(v.id);
            return pending ? { ...v, value: pending.value } : v;
        });
        const mergedTexts = new Set(merged.map(v => String(v.value ?? '').toLowerCase()));
        const extras = pendingValues.filter(v =>
            !serverIds.has(v.id) && !mergedTexts.has(String(v.value ?? '').toLowerCase())
        );
        return [...merged, ...extras];
    }, [feature.rawValues, pendingValues]);
    const hasValues = displayValues.length > 0;
    const { confirmDelete, modal: deleteUsageModal } = useUsageDeleteConfirm();
    const notifyRuleCellsChanged = onRuleCellsChanged || onRefresh;

    const handleDeleteFeature = async (id, name) => {
        if (!await confirmDelete({ kind: 'feature', featureId: id, name })) return;
        try {
            const res = await apiFetch(`/api/features/${id}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                notifyRuleCellsChanged();
            } else {
                const text = await res.text();
                alert(`Failed to delete feature: ${text}`);
            }
        } catch (err) {
            console.error(err);
            alert("Failed to connect to the Rule Engine server.");
        }
    };

    const handleSaveValRename = async (valId) => {
        const text = editingValText.trim();
        if (!text) return;

        const previousValue = displayValues.find(v => v.id === valId)?.value;
        setPendingValues(prev => {
            const exists = prev.some(v => v.id === valId);
            if (exists) return prev.map(v => v.id === valId ? { ...v, value: text } : v);
            return [...prev, { id: valId, value: text }];
        });
        setEditingValId(null);

        const revertRename = () => {
            setPendingValues(prev => {
                if (previousValue === undefined) return prev.filter(v => v.id !== valId);
                const exists = prev.some(v => v.id === valId);
                if (exists) return prev.map(v => v.id === valId ? { ...v, value: previousValue } : v);
                return prev;
            });
        };

        try {
            const res = await apiFetch(`/api/features/${feature.id}/values/${valId}/rename`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Value: text })
            });
            if (res.ok) {
                notifyRuleCellsChanged();
            } else {
                const errText = await res.text();
                revertRename();
                alert(`Failed to rename value: ${errText}`);
                notifyRuleCellsChanged();
            }
        } catch (err) {
            console.error(err);
            revertRename();
            alert("Failed to connect to the Rule Engine server.");
            notifyRuleCellsChanged();
        }
    };

    const handleDeleteVal = async (valId, valText) => {
        if (!await confirmDelete({ kind: 'value', featureId: feature.id, valueId: valId, name: valText })) return;
        try {
            const res = await apiFetch(`/api/features/${feature.id}/values/${valId}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                notifyRuleCellsChanged();
            } else {
                const text = await res.text();
                alert(`Failed to delete value: ${text}`);
            }
        } catch (err) {
            console.error(err);
            alert("Failed to connect to the Rule Engine server.");
        }
    };

    const handleAddVal = async (e) => {
        e.preventDefault();
        const text = newValText.trim();
        if (!text) return;

        const tempId = (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? `temp-${crypto.randomUUID()}`
            : `temp-${Date.now()}`;
        const optimistic = { id: tempId, value: text };
        setPendingValues(prev => [...prev, optimistic]);
        setNewValText('');
        setIsAddingValue(false);
        setIsExpanded(true);

        try {
            const res = await apiFetch(`/api/features/${feature.id}/values`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Value: text })
            });
            if (res.ok) {
                const data = await res.json();
                const realId = data.id || data.Id;
                const realValue = data.value || data.Value || text;
                setPendingValues(prev => prev.map(v =>
                    v.id === tempId ? { ...v, id: realId || v.id, value: realValue } : v
                ));
                onRefresh();
            } else {
                const errText = await res.text();
                setPendingValues(prev => prev.filter(v => v.id !== tempId));
                alert(`Failed to add value: ${errText}`);
                onRefresh();
            }
        } catch (err) {
            console.error(err);
            setPendingValues(prev => prev.filter(v => v.id !== tempId));
            alert("Failed to connect to the Rule Engine server.");
            onRefresh();
        }
    };

    const featureActionItems = [
        onSearchFeature && {
            label: 'Find tables with this feature',
            icon: 'fa-solid fa-magnifying-glass',
            onSelect: () => onSearchFeature(feature.fullName || feature.name)
        },
        {
            label: 'Manage Feature',
            icon: 'fa-solid fa-pen',
            onSelect: () => setShowEditModal(true)
        },
        {
            label: 'Add value',
            icon: 'fa-solid fa-plus',
            onSelect: () => {
                setIsAddingValue(true);
                setIsExpanded(true);
            }
        },
        {
            label: 'Delete Feature',
            icon: 'fa-solid fa-trash-can',
            danger: true,
            onSelect: () => handleDeleteFeature(feature.id, feature.name)
        }
    ].filter(Boolean);

    const valueActionItems = (val, displayValue) => [
        onSearchValue && {
            label: 'Find tables with this value',
            icon: 'fa-solid fa-magnifying-glass',
            onSelect: () => onSearchValue(feature.fullName || feature.name, displayValue)
        },
        {
            label: 'Rename Value',
            icon: 'fa-solid fa-pen',
            onSelect: () => {
                setEditingValId(val.id);
                setEditingValText(displayValue);
            }
        },
        {
            label: 'Delete Value',
            icon: 'fa-solid fa-trash-can',
            danger: true,
            onSelect: () => handleDeleteVal(val.id, displayValue)
        }
    ].filter(Boolean);

    return (
        <div style={{ marginLeft: '0.5rem' }}>
            <div 
                onClick={() => hasValues && setIsExpanded(!isExpanded)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.35rem', 
                    cursor: hasValues ? 'pointer' : 'default', 
                    padding: '0.15rem 0.25rem',
                    borderRadius: '4px',
                    backgroundColor: isHovered ? 'var(--bg-secondary)' : 'transparent',
                    color: 'var(--text-secondary)',
                    transition: 'var(--transition)'
                }}
            >
                {hasValues ? (
                    <i className={`fa-solid ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} style={{ width: '0.7rem', fontSize: '0.7rem', color: 'var(--text-secondary)', flexShrink: 0 }}></i>
                ) : (
                    <span style={{ width: '0.7rem', flexShrink: 0 }} />
                )}
                <i className="fa-solid fa-cube" style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}></i>
                <span style={{ color: 'var(--text-primary)', fontWeight: '500', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{feature.name}</span>
                {feature.isGlobal && (
                    <i className="fa-solid fa-globe" title="Global Feature" style={{ color: 'var(--accent)', fontSize: '0.8rem', flexShrink: 0 }}></i>
                )}
                <span style={{
                    flexShrink: 0,
                    opacity: (isHovered || featureMenuOpen) ? 1 : 0,
                    pointerEvents: (isHovered || featureMenuOpen) ? 'auto' : 'none',
                    transition: 'var(--transition)'
                }}>
                    <ActionsMenu
                        compact
                        label={`${feature.name} actions`}
                        items={featureActionItems}
                        onOpenChange={setFeatureMenuOpen}
                    />
                </span>
            </div>

            {(isExpanded || isAddingValue) && (
                <div style={{ marginLeft: '1.5rem', display: 'flex', flexDirection: 'column', gap: 0, paddingLeft: '8px', borderLeft: '1px dotted var(--border)' }}>
                    {sortFeatureValues(displayValues).map(val => {
                        const displayValue = val.value !== undefined && val.value !== null ? String(val.value) : '';
                        return (
                            <div 
                            key={val.id} 
                            onMouseEnter={() => setHoveredValId(val.id)}
                            onMouseLeave={() => setHoveredValId(null)}
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between', 
                                gap: '0.25rem',
                                fontSize: '0.85rem', 
                                color: 'var(--text-secondary)', 
                                padding: '0.1rem 0.15rem',
                                marginLeft: '-0.35rem',
                                borderRadius: '4px',
                                backgroundColor: hoveredValId === val.id ? 'var(--bg-secondary)' : 'transparent',
                                transition: 'var(--transition)'
                            }}
                        >
                            {editingValId === val.id ? (
                                <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', width: '100%' }}>
                                    <input 
                                        className="input-field" 
                                        value={editingValText} 
                                        onChange={e => setEditingValText(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleSaveValRename(val.id);
                                            }
                                            if (e.key === 'Escape') {
                                                e.preventDefault();
                                                setEditingValId(null);
                                            }
                                        }}
                                        style={{ fontSize: '0.8rem', padding: '2px 4px', width: '120px' }}
                                        autoFocus
                                    />
                                    <button 
                                        onClick={() => handleSaveValRename(val.id)}
                                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--accent)', padding: 0 }}
                                        title="Save Rename"
                                    >
                                        <i className="fa-solid fa-check"></i>
                                    </button>
                                    <button 
                                        onClick={() => setEditingValId(null)}
                                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 0 }}
                                        title="Cancel"
                                    >
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', minWidth: 0, flex: 1 }}>
                                        <i className="fa-solid fa-circle" style={{ fontSize: '0.3rem', color: 'var(--text-secondary)', opacity: 0.5 }}></i>
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayValue}</span>
                                    </div>
                                    <span style={{
                                        flexShrink: 0,
                                        opacity: (hoveredValId === val.id || openValMenuId === val.id) ? 1 : 0,
                                        pointerEvents: (hoveredValId === val.id || openValMenuId === val.id) ? 'auto' : 'none',
                                        transition: 'var(--transition)'
                                    }}>
                                        <ActionsMenu
                                            compact
                                            label={`${displayValue} actions`}
                                            items={valueActionItems(val, displayValue)}
                                            onOpenChange={(open) => setOpenValMenuId(open ? val.id : null)}
                                        />
                                    </span>
                                </>
                            )}
                        </div>
                        );
                    })}
                    
                    {isAddingValue && (
                        <form onSubmit={handleAddVal} style={{ display: 'flex', gap: '0.25rem', marginTop: '0.25rem' }}>
                            <input 
                                className="input-field" 
                                placeholder="Add value..." 
                                value={newValText} 
                                onChange={e => setNewValText(e.target.value)}
                                style={{ fontSize: '0.8rem', padding: '2px 4px', width: '100px' }}
                                autoFocus
                            />
                            <button 
                                type="submit" 
                                className="btn-secondary" 
                                disabled={!newValText.trim()}
                                style={{ padding: '2px 6px', fontSize: '0.75rem' }}
                            >
                                Add
                            </button>
                        </form>
                    )}
                </div>
            )}

            {deleteUsageModal}

            {showEditModal && (
                <ManageFeatureModal 
                    feature={feature.raw || {
                        id: feature.id,
                        name: feature.fullName || feature.name,
                        values: feature.rawValues || feature.values || []
                    }}
                    familyId={familyId}
                    onClose={() => {
                        setShowEditModal(false);
                        onRefresh();
                    }}
                    onRuleCellsChanged={notifyRuleCellsChanged}
                />
            )}
        </div>
    );
}
